import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// 테마 정의 - 과학적 색상 이론 기반
export const THEMES = {
  // 기본 다크 테마 - 클레이 네이비 + 브랜드 민트 (귀여움 업그레이드, 클레이 아이콘 배경과 정합. 이전의 dark ID 유지)
  dark: {
    id: 'dark',
    name: 'themeHell',
    description: 'themeHellDesc',
    preview: ['#131a2b', '#1e2740', '#78E6C8'],
    colors: {
      '--bg': '#131a2b',
      '--card': '#1e2740',
      '--surface': '#2a3552',
      '--border': '#3d4a6b',
      '--text': '#F1F5F9',
      '--text-secondary': '#E2E8F0',
      '--text-muted': '#94A3B8',
      '--accent': '#78E6C8', // 브랜드 민트 (바알시불/클레이 정체성)
      '--accent-dark': '#4FD1B3',
      '--accent-rgb': '120, 230, 200',
      '--primary-soft': 'rgba(120, 230, 200, 0.15)',
      '--card-rgb': '30, 39, 64',
      '--surface-rgb': '42, 53, 82',
      '--border-rgb': '61, 74, 107',
    },
  },
  // AMOLED 다크 - 지옥의 순수 블랙 (OLED 배터리 절약)
  amoled: {
    id: 'amoled',
    name: 'themeAmoled',
    description: 'themeAmoledDesc',
    preview: ['#000000', '#0f0f0f', '#ef4444'],
    colors: {
      '--bg': '#000000',
      '--card': '#0f0f0f',
      '--surface': '#1f1515',
      '--border': '#3a2424',
      '--text': '#FFFFFF',
      '--text-secondary': '#E2E8F0',
      '--text-muted': '#888888',
      '--accent': '#ff5b5b',
      '--accent-dark': '#ef4444',
      '--accent-rgb': '255, 91, 91',
      '--primary-soft': 'rgba(255, 91, 91, 0.15)',
      '--card-rgb': '15, 15, 15',
      '--surface-rgb': '31, 21, 21',
      '--border-rgb': '58, 36, 36',
    },
  },
  // 미드나잇 민트 - 기존의 다크 블루/민트그린 테마
  midnight: {
    id: 'midnight',
    name: 'themeMidnight',
    description: 'themeMidnightDesc',
    preview: ['#111a2e', '#223047', '#78E6C8'],
    colors: {
      '--bg': '#111a2e',
      '--card': '#223047',
      '--surface': '#34465e',
      '--border': '#4e637f',
      '--text': '#F1F5F9',
      '--text-secondary': '#94A3B8',
      '--text-muted': '#64748B',
      '--accent': '#78E6C8',
      '--accent-dark': '#4FD1B3',
      '--accent-rgb': '120, 230, 200',
      '--primary-soft': 'rgba(120, 230, 200, 0.15)',
      '--card-rgb': '34, 48, 71',
      '--surface-rgb': '52, 70, 94',
      '--border-rgb': '78, 99, 127',
    },
  },
  // 네이비 다크 - 푸른 계열 (집중력 향상)
  navy: {
    id: 'navy',
    name: 'themeNavy',
    description: 'themeNavyDesc',
    preview: ['#0D1B2A', '#1B263B', '#3b82f6'],
    colors: {
      '--bg': '#0D1B2A',
      '--card': '#1B263B',
      '--surface': '#415A77',
      '--border': '#778DA9',
      '--text': '#E0E1DD',
      '--text-secondary': '#A8DADC',
      '--text-muted': '#778DA9',
      '--accent': '#3b82f6',
      '--accent-dark': '#1d4ed8',
      '--accent-rgb': '59, 130, 246',
      '--primary-soft': 'rgba(59, 130, 246, 0.15)',
      '--card-rgb': '27, 38, 59',
      '--surface-rgb': '65, 90, 119',
      '--border-rgb': '119, 141, 169',
    },
  },
  // 따뜻한 다크 - 갈색 계열 (눈 편안)
  warm: {
    id: 'warm',
    name: 'themeWarm',
    description: 'themeWarmDesc',
    preview: ['#1A1612', '#2D261E', '#E8B059'],
    colors: {
      '--bg': '#1A1612',
      '--card': '#2D261E',
      '--surface': '#3D342A',
      '--border': '#5C4D3C',
      '--text': '#F5E6D3',
      '--text-secondary': '#C9B99A',
      '--text-muted': '#8A7A66',
      '--accent': '#E8B059',
      '--accent-dark': '#D4973D',
      '--accent-rgb': '232, 176, 89',
      '--primary-soft': 'rgba(232, 176, 89, 0.15)',
      '--card-rgb': '45, 38, 30',
      '--surface-rgb': '61, 52, 42',
      '--border-rgb': '92, 77, 60',
    },
  },
  // 라이트 테마 - 밝은 모드
  light: {
    id: 'light',
    name: 'themeLight',
    description: 'themeLightDesc',
    preview: ['#F8FAFC', '#FFFFFF', '#ef4444'],
    colors: {
      '--bg': '#F8FAFC',
      '--card': '#FFFFFF',
      '--surface': '#F1F5F9',
      '--border': '#E2E8F0',
      '--text': '#0F172A',
      '--text-secondary': '#475569',
      '--text-muted': '#94A3B8',
      '--accent': '#ef4444',
      '--accent-dark': '#b91c1c',
      '--accent-rgb': '239, 68, 68',
      '--primary-soft': 'rgba(239, 68, 68, 0.1)',
      '--card-rgb': '255, 255, 255',
      '--surface-rgb': '241, 245, 249',
      '--border-rgb': '226, 232, 240',
    },
  },
  // 보라 다크 - 게이밍 느낌
  purple: {
    id: 'purple',
    name: 'themePurple',
    description: 'themePurpleDesc',
    preview: ['#171522', '#252136', '#A855F7'],
    colors: {
      '--bg': '#171522',
      '--card': '#252136',
      '--surface': '#342e4a',
      '--border': '#4e456d',
      '--text': '#F3F0FF',
      '--text-secondary': '#C4B5FD',
      '--text-muted': '#7C72A0',
      '--accent': '#A855F7',
      '--accent-dark': '#9333EA',
      '--accent-rgb': '168, 85, 247',
      '--primary-soft': 'rgba(168, 85, 247, 0.15)',
      '--card-rgb': '37, 33, 54',
      '--surface-rgb': '52, 46, 74',
      '--border-rgb': '78, 69, 109',
    },
  },
};

export type ThemeId = keyof typeof THEMES;
export type Theme = (typeof THEMES)[ThemeId];

interface ThemeContextType {
  currentTheme: ThemeId;
  theme: Theme;
  setTheme: (themeId: ThemeId) => void;
  themes: typeof THEMES;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'hellgater-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<ThemeId>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved && saved in THEMES) {
        return saved as ThemeId;
      }
    }
    return 'dark';
  });

  // 테마 CSS 변수 적용
  useEffect(() => {
    const theme = THEMES[currentTheme];
    const root = document.documentElement;

    // CSS 변수 설정
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // body 클래스 설정 (Tailwind용)
    document.body.className = `theme-${currentTheme}`;

    // localStorage에 저장
    localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
  }, [currentTheme]);

  const setTheme = (themeId: ThemeId) => {
    if (themeId in THEMES) {
      setCurrentTheme(themeId);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        theme: THEMES[currentTheme],
        setTheme,
        themes: THEMES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
